import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { a3Copy } from "@/components/assignments/a3-copy";
import { useMaterialLibrary } from "@/components/courses/materials/use-material-library";
import { Button } from "@/components/ui/button";
import { courseMaterialsApi } from "@/lib/api/courseMaterials";
import { courseUnitsApi } from "@/lib/api/courseUnits";
import type { SessionWorkspace } from "@/lib/api/sessionPortal";
import type { SessionPlanParts } from "./ClassPlanCard";
import { PlanTaskField } from "./SessionPlanFields";
import { closeOwnsEdit, PLAN_FIELDS, planWords, planFailureWords } from "./session-plan-content";
import { checkReviewedRecords, publishReviewedRecords, PublicationFailure, type OutgoingRecord, type PublicationResult } from "./session-publication";
import { SESSION_COPY } from "./boxes/session-copy";
import type { Blocker, BoxState, Effect, Part, Read, Result, TaskBox } from "@/components/ui/box.schema";
import type { CourseUnit } from "@/lib/api/courseUnits";
import { Register, RegisterRow, RegisterTitle } from "@/components/ui/register";
import { SessionNewRecord } from "./SessionNewRecord";
import { ReadingRow } from "./SessionPrepareRows";

export type ReportBlocked = (id: string, blocked: boolean) => void;

type PrepareValue = { unit: CourseUnit | null };

export interface PrepareBox {
  box: TaskBox<PrepareValue>;
  /** The page's report that Prepare opened or hid. */
  onOpenChange: (open: boolean) => void;
}

/**
 * S01 Prepare: one Task whose parts are what learners see before class, in
 * order: the session plan's guidance, then its readings (O2). The plan is
 * edited where it is drawn (O1) and saved as a server draft (O7). One commit
 * publishes the chosen guidance and material references, each a named choice
 * in the foot (O3); a failure on some is Partly done and retries only those
 * (W1), and a lost answer is checked by reading each record back.
 *
 * Coursework is not published here: it publishes from its canonical author,
 * with that author's key and expected version (A2). The running order and
 * teaching notes are the teaching team's own boxes after this one.
 */
export function usePrepareBox({ workspace, parts, rank }: { workspace: SessionWorkspace; parts: SessionPlanParts; rank: number | null }): PrepareBox {
  const copy = SESSION_COPY.prepare;
  const { unit, state, notices } = parts;
  const { course, session, viewer } = workspace;
  const library = useMaterialLibrary(course.id, { unitId: unit?.id ?? "", timezone: course.timezone });
  const queryClient = useQueryClient();
  const currentState = useRef(state); currentState.current = state;
  const [adding, setAdding] = useState(false);
  const [blockedRows, setBlockedRows] = useState(new Set<string>());
  const [skipped, setSkipped] = useState(new Set<string>());
  const [result, setResult] = useState<Result>({ state: "none" });
  const [results, setResults] = useState<Record<string, PublicationResult>>({});
  const report = useCallback<ReportBlocked>((id, blocked) => setBlockedRows(current => {
    if (current.has(id) === blocked) return current;
    const next = new Set(current); if (blocked) next.add(id); else next.delete(id); return next;
  }), []);
  const addReading = useRef<HTMLButtonElement>(null);
  const editable = course.term_open && !state?.refused && viewer.allowed_actions.includes("manage_session_coursework");
  const records: OutgoingRecord[] = [];
  const planReason = !state ? undefined : state.storageError ? "Browser storage is unavailable. The plan can't be saved safely."
    : state.pending ? state.pending.kind === "save" ? "The plan's last save isn't confirmed. Check it before publishing." : undefined
    : state.busy || state.dirty ? "Publishing the plan waits until your latest edits are saved."
    : state.conflict || state.comparisonUnavailable ? "Resolve the newer plan version first."
    : !unit ? "Publishing needs a saved plan. Start with the session topic."
    : unit.can_publish !== true ? "The primary instructor publishes guidance. Your draft is saved."
    : unit.learning_objectives.length === 0 ? "Add a session objective before publishing." : undefined;
  const prepareFields = PLAN_FIELDS.filter(field => ["topic", "objectives", "preparation"].includes(field.key));
  const planUnpublished = !!state && (!unit?.publication || prepareFields.some(field => (state.draft[field.key]?.trim() ?? planWords(unit?.[field.column])) !== planWords(unit?.publication?.[field.column])));
  if (unit && state && planUnpublished && session.status !== "held") {
    const reviewed = unit;
    records.push({ id: `plan:${unit.id}`, title: copy.plan, blockedReason: planReason, scope: "", content: [],
      inspect: async () => {
        if (currentState.current?.pending) {
          await currentState.current.reconcile();
          if (currentState.current?.pending) throw new Error("The plan request is still unconfirmed");
        }
        const latest = (await courseUnitsApi.list(course.id)).find(item => item.id === reviewed.id);
        if (!latest) return "changed";
        if (latest.published_revision === reviewed.saved_revision) return "published";
        return latest.saved_revision === reviewed.saved_revision && latest.revision === reviewed.revision ? "draft" : "changed";
      },
      publish: async () => {
        const owner = currentState.current;
        if (!owner || owner.dirty || owner.disabled || owner.conflict || owner.acknowledged?.revision !== reviewed.revision) throw new PublicationFailure("refused", "The plan changed or isn't ready. Save the current edits and resolve any newer version. Nothing was sent.");
        const outcome = await owner.publishOutcome();
        if (outcome.kind !== "confirmed") throw new PublicationFailure(outcome.kind, planFailureWords(outcome.reason));
      },
    });
  }
  if (editable) for (const item of library.items.filter(item => !item.published)) {
    const material = item.material;
    records.push({ id: `reading:${material.id}`, title: material.title, blockedReason: unit?.can_publish !== true ? a3Copy.primaryPublishesResources : blockedRows.has(material.id) ? "Finish saving this reading first." : library.isError ? "This reading couldn't be refreshed. Read it again first." : undefined, scope: "", content: [],
      dependency: unit?.status === "published" ? undefined : copy.waitsForPlan,
      inspect: async () => {
        const [materials, placements] = await Promise.all([courseMaterialsApi.getMaterials(course.id), courseMaterialsApi.getResourcePlacements(course.id)]);
        const latest = materials.find(row => row.id === material.id);
        if (!latest || latest.updated_at !== material.updated_at) return "changed";
        const placement = placements.find(row => row.reference_kind === "material" && row.material_id === material.id);
        if (placement?.publication_status === "published") return "published";
        if ((placement?.revision ?? null) !== (item.placement?.revision ?? null)) return "changed";
        return "draft";
      },
      publish: () => item.placement ? courseMaterialsApi.patchResourcePlacement(course.id, item.placement.id, { revision: item.placement.revision, publication_status: "published" }) : courseMaterialsApi.createResourcePlacement(course.id, { reference_kind: "material", placement_role: "course_content", material_id: material.id, position: null, publication_status: "published" }),
    });
  }
  const partOf = (record: OutgoingRecord) => record.id.startsWith("plan:") ? "plan" : "readings";
  const chosen = records.filter(record => !skipped.has(record.id));
  const refresh = () => { library.refresh(); void queryClient.invalidateQueries({ queryKey: ["session-workspace"] }); };
  const closeAdd = () => {
    setAdding(false);
    requestAnimationFrame(() => addReading.current?.focus());
  };
  const at = () => new Date().toISOString();
  const titles = (list: OutgoingRecord[]) => list.map(record => record.title).join(", ");

  const settle = (sent: OutgoingRecord[], next: Record<string, PublicationResult>, failures: Record<string, string>, requestId: string): Result => {
    setResults(next);
    const published = sent.filter(record => next[record.id] === "published");
    const failed = sent.filter(record => next[record.id] === "failed" || next[record.id] === "changed");
    const uncertain = sent.filter(record => next[record.id] === "uncertain");
    const remains = published.some(record => record.dependency) && !published.some(record => record.id.startsWith("plan:")) ? [copy.waitsForPlan] : [];
    if (uncertain.length) return {
      state: "unknown", cap: copy.unknownCap, requestId, sentence: copy.unknownSentence(titles(uncertain)),
      check: { verb: copy.check, note: copy.checkNote, run: async () => {
        const checked = await checkReviewedRecords(sent, next);
        const again = settle(sent, checked, failures, requestId);
        refresh(); setResult(again); return again;
      } },
    };
    if (failed.length) return {
      state: "partial", at: at(), requestId,
      went: published.map(record => ({ sentence: copy.publishedLine(record.title), ids: [record.id] })),
      failed: failed.map(record => ({ sentence: copy.failedLine(record.title, failures[record.id] ?? ""), ids: [record.id], fix: partOf(record) })),
      retry: { verb: copy.retry(failed.length), note: copy.retryNote, run: async () => {
        const retryFailures = { ...failures };
        const reset = Object.fromEntries(Object.entries(next).filter(([, value]) => value !== "failed"));
        const again = await publishReviewedRecords(sent, new Set(failed.map(record => record.id)), reset, (id, message) => { retryFailures[id] = message; });
        const settled = settle(sent, again, retryFailures, requestId);
        refresh(); setResult(settled); return settled;
      } },
    };
    return { state: "confirmed", at: at(), effects: [copy.publishedLine(titles(published))], remains };
  };

  const run = async (requestId: string): Promise<Result> => {
    const sent = chosen.filter(record => !record.blockedReason);
    const failures: Record<string, string> = {};
    const next = await publishReviewedRecords(sent, new Set(sent.map(record => record.id)), {}, (id, message) => { failures[id] = message; });
    const settled = settle(sent, next, failures, requestId);
    if (settled.state === "confirmed") setSkipped(new Set());
    refresh(); setResult(settled);
    return settled;
  };

  const read: Read<PrepareValue> = parts.read === "loading" ? { status: "loading" }
    : parts.read === "failed" ? { status: "failed", sentence: copy.planUnread, retry: parts.retry ?? (() => undefined) }
    : parts.read === "forbidden" ? { status: "forbidden" }
    : { status: "ready", value: { unit }, readAt: at() };
  const pendingRecords = records.length > 0;
  const unpublishedReadings = library.items.filter(item => !item.published).length;
  // The face names what differs from what learners see: the plan, its readings, or both.
  // A held session no longer offers the plan for publishing, but still says it isn't.
  const boxState = (): BoxState => result.state === "unknown" ? { class: "reading", word: copy.notConfirmed }
    : result.state === "partial" ? { class: "failed", word: copy.someFailed(result.failed.length) }
    // An unconfirmed plan save outranks what is unpublished: its Retry is inside, so a folded face must say so (K3, W2).
    : state?.pending?.kind === "save" ? { class: "reading", word: copy.saveNotConfirmed }
    : !unit ? { class: "waiting", word: copy.noPlan }
    : planUnpublished || unpublishedReadings ? { class: pendingRecords && !unit.publication ? "waiting" : "draft", word: copy.unpublished(planUnpublished && (unit.publication ? "changed" : "new"), unpublishedReadings) }
    // Published is a claim about the readings too, so it waits for their read and never survives its failure.
    : library.isError ? { class: "failed", word: copy.readingsUnread }
    : library.isLoading ? { class: "reading", word: copy.readingReadings }
    : { class: "done", word: copy.published };
  const canPrepare = course.term_open && !!state && !state.refused;
  const rowResult = (id: string) => results[id];

  const planPart: Part = {
    id: "plan", label: copy.plan, owner: { system: "imam" }, fold: "never", draft: "server",
    state: !unit ? { class: "waiting", word: copy.noPlan } : planUnpublished ? { class: "draft", word: copy.changed } : { class: "done", word: copy.published },
    markedByBox: true,
    render: () => <div className="grid gap-4">
      {!closeOwnsEdit(parts.saveField) && <div className="empty:hidden">{notices}</div>}
      {!parts.unavailable && <>
        {parts.planAction && <div className="flex justify-end">{parts.planAction}</div>}
        {(["topic", "objectives", "preparation"] as const).map(key => <PlanTaskField key={key} fieldKey={key} unit={unit} state={state} sessionId={session.id} onEdit={parts.onEditField} showSaveStatus={parts.saveField === key} />)}
      </>}
    </div>,
  };
  const readingsPart: Part = {
    id: "readings", label: copy.readings, owner: { system: "imam" }, fold: "never", draft: "server",
    // An empty list has nothing published; it says None, not Published.
    state: library.isError ? { class: "failed", word: copy.readingsUnread } : library.isLoading ? { class: "reading", word: copy.readingReadings }
      : unpublishedReadings ? { class: "draft", word: copy.notPublished } : library.items.length ? { class: "done", word: copy.published } : { class: "quiet", word: copy.none },
    render: () => <div className="grid gap-2">
      {!unit && <p className="text-meta text-muted-foreground">{copy.noRecords}</p>}
      {!course.term_open && <p className="text-meta text-muted-foreground">{copy.termClosed}</p>}
      {editable && <div><Button ref={addReading} variant="subtle" size="sm" disabled={!unit || !!adding} onClick={() => setAdding(true)}>{copy.addReading}</Button></div>}
      <Register rowsOnly template="minmax(0,1fr) auto" aria-label={copy.readings} className="-ms-5 -me-4">
        {adding && unit && <SessionNewRecord library={library} unitId={unit.id} onClose={closeAdd} onCreated={library.refresh} />}
        {!editable ? workspace.plan.unit?.materials?.map(item => <RegisterRow key={item.id}><div><RegisterTitle>{item.title}</RegisterTitle></div>{item.file_url && <a className="inline-flex min-h-11 items-center text-ui underline" href={item.file_url} target="_blank" rel="noopener noreferrer">Open<span className="sr-only"> {item.title} in a new tab</span></a>}</RegisterRow>)
          : library.isError ? <li className="px-5 py-3"><p className="text-ui">{copy.readingsFailed}</p><Button variant="outline" onClick={library.retry}>{copy.retryReadings}</Button></li>
          : library.items.map(item => <ReadingRow key={library.rowKey(item.material.id)} item={item} library={library} report={report} editable={editable && (!item.published || unit?.can_publish === true)} result={rowResult(`reading:${item.material.id}`)} />)}
        {!library.isLoading && !library.isError && library.items.length === 0 && <li className="px-5 py-3 text-meta text-muted-foreground">{copy.noReadings}</li>}
      </Register>
      <p className="text-meta text-muted-foreground">{copy.driveNote}</p>
    </div>,
  };
  const blockers = (): Blocker[] => {
    const blocked = chosen.find(record => record.blockedReason);
    if (blocked) return [{ kind: "missing", part: partOf(blocked), sentence: copy.blockedTitle(blocked.title, blocked.blockedReason!) }];
    if (!chosen.length) return [{ kind: "missing", part: records.length ? partOf(records[0]) : "plan", sentence: copy.chooseSome }];
    return [];
  };
  // A confirmed publish folds Prepare to its result alone (CASE-015). Opening
  // it again starts the next change, so the result gives way to the parts, as
  // the roll's does; the face still says Published from the records.
  const onOpenChange = (open: boolean) => { if (open && result.state === "confirmed") setResult({ state: "none" }); };

  const box: TaskBox<PrepareValue> = {
    kind: "task",
    id: "prepare",
    job: copy.job,
    label: copy.label,
    owner: { system: "imam" },
    read,
    state: boxState,
    detail: () => copy.detail(state?.draft.topic || unit?.topic || copy.plan, library.isLoading || library.isError ? null : library.items.length),
    act: canPrepare && (pendingRecords || !unit) ? { verb: unit ? copy.continue : copy.start, does: "open", opens: "plan" } : undefined,
    candidate: canPrepare && (pendingRecords || !unit) ? { eligible: true, blocked: null, rank } : undefined,
    // Published with nothing left to send, Prepare has no act; a quiet Change still opens it, to add a reading or edit the plan.
    exits: canPrepare && unit && !pendingRecords ? [{ label: copy.change, reopens: true }] : undefined,
    fold: "settled",
    parts: [planPart, readingsPart],
    commit: {
      verb: copy.commit,
      cap: copy.cap,
      effects: () => records.map((record): Effect => ({
        id: record.id,
        sentence: record.id.startsWith("plan:") ? copy.publishPlan : copy.publishRecord(record.title),
        part: partOf(record),
        optional: { checked: !skipped.has(record.id), toggle: () => setSkipped(current => { const next = new Set(current); if (next.has(record.id)) next.delete(record.id); else next.add(record.id); return next; }) },
      })),
      blockers,
      note: copy.note,
      run,
    },
    result,
    draft: !!state?.dirty || blockedRows.size > 0 || !!adding,
  };
  return { box, onOpenChange };
}
